import { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { Actions } from "../../../core/modules/Actions";
import {
  Box,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  DialogContent,
  ListItemText,
  OutlinedInput,
  Grid,
  Chip,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import { IconButtonComponent, TextIconButtonComponent, PaginationTable } from "../../../component/atom";
import DialogComponent from "../../../component/atom/Dialog/Dialog";
import usePolling from "../../../hook/usePolling";
import { SubtitleProgressBackdrop, PopUpMessageComponent } from "../../../component/molecule";

// Mock available courses with progress and levels to match dashboard screenshot

const VideoSubtitleScreen = ({
  courseSubtitleList = [],
  languagesList = [],
  fetchCourseSubtitleList = () => { },
  generateSubtitles = () => { },
  fetchLanguages = () => { },
}) => {
  // Main list of courses state
  const [courses, setCourses] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [stopPolling, setStopPolling] = useState(false);

  const searchTimeoutRef = useRef(null);
  const wasProcessingRef = useRef(false);

  const fetchSubtitles = (page, perPage, search = "") => {
    return fetchCourseSubtitleList({
      page,
      per_page: perPage,
      search: search ? search.trim() : "",
    });
  };

  // Initial fetch on mount (fires exactly once)
  useEffect(() => {
    fetchSubtitles(1, rowsPerPage, "");
    fetchLanguages();
  }, []);

  // Periodic polling every 5 seconds for the current page if there are items in progress or pending
  const processingInfo = useMemo(() => {
    const processingCourse = courses.find(
      (course) => course.status?.toLowerCase() === "processing"
    );

    if (!processingCourse) {
      return {
        hasProcessing: false,
        completed: 0,
        total: 0,
        percent: 0,
      };
    }

    const completed = processingCourse.completed_videos !== undefined
      ? processingCourse.completed_videos
      : (processingCourse.progressCurrent || 0);

    const total = processingCourse.total_videos !== undefined
      ? processingCourse.total_videos
      : (processingCourse.progressTotal || 0);

    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      hasProcessing: true,
      completed,
      total,
      percent,
    };
  }, [courses]);

  const isPollingActive = processingInfo.hasProcessing && !stopPolling && !errorOpen;

  useEffect(() => {
    if (wasProcessingRef.current && !processingInfo.hasProcessing) {
      const hasFailed = courses.some(
        (c) =>
          c.status?.toLowerCase() === "failed" ||
          c.status?.toLowerCase() === "error" ||
          c.status?.toLowerCase() === "cancelled"
      );
      if (hasFailed) {
        setStopPolling(true);
        setErrorOpen(true);
      } else {
        setSuccessOpen(true);
      }
    }
    wasProcessingRef.current = processingInfo.hasProcessing;
  }, [processingInfo.hasProcessing, courses]);

  usePolling(
    () => {
      return fetchSubtitles(currentPage, rowsPerPage, searchQuery)
        .then((res) => {
          if (res && res.error) {
            setStopPolling(true);
            setErrorOpen(true);
          }
        })
        .catch((err) => {
          console.error("Subtitle polling failed:", err);
          setStopPolling(true);
          setErrorOpen(true);
        });
    },
    isPollingActive ? 5000 : null,
    [currentPage, rowsPerPage, searchQuery, isPollingActive],
    false
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchSubtitles(newPage, rowsPerPage, searchQuery);
  };

  const handleRowsPerPageChange = (newPerPage) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
    fetchSubtitles(1, newPerPage, searchQuery);
  };

  const handleSearchChange = (searchText) => {
    setSearchQuery(searchText);
    setCurrentPage(1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchSubtitles(1, rowsPerPage, searchText);
    }, 400);
  };


  useEffect(() => {
    if (courseSubtitleList) {
      const rawData = courseSubtitleList.data?.data || courseSubtitleList.data || (Array.isArray(courseSubtitleList) ? courseSubtitleList : []);
      const mappedCourses = rawData.map(course => {
        const completed = course.completed_videos !== undefined ? course.completed_videos : (course.progressCurrent || 0);
        const total = course.total_videos !== undefined ? course.total_videos : (course.progressTotal || 0);

        return {
          id: course.course_id || course.id,
          course_id: course.course_id || course.id,
          name: course.course_name || course.name || "",
          lessons: total,
          level: course.level || "Course Level",
          chosenLanguages: typeof course.configured_languages === 'string'
            ? course.configured_languages.split(',').map(s => s.trim()).filter(Boolean)
            : Array.isArray(course.chosen_languages)
              ? course.chosen_languages
              : typeof course.chosen_languages === 'string'
                ? course.chosen_languages.split(',').map(s => s.trim()).filter(Boolean)
                : course.chosenLanguages || [],
          progressCurrent: completed,
          progressTotal: total,
          completed_videos: completed,
          total_videos: total,
          status: course.subtitle_status
        };
      });
      setCourses(mappedCourses);

      const total = courseSubtitleList.data?.total || courseSubtitleList.total || 0;
      setTotalItems(total);

      const perPage = Number(courseSubtitleList.data?.per_page || courseSubtitleList.per_page);
      if (perPage && perPage !== rowsPerPage) {
        setRowsPerPage(perPage);
      }
    } else {
      setCourses([]);
      setTotalItems(0);
    }
  }, [courseSubtitleList]);

  useEffect(() => {
    if (languagesList && languagesList.length > 0) {
      const mapped = languagesList.map(lang => {
        const isEnglish = lang.name?.toLowerCase() === "english";
        return {
          id: lang.code,
          name: lang.name,
          code: lang.code,
          selected: isEnglish,
          disabled: isEnglish
        };
      });
      setAvailableLanguages(mapped);
    } else {
      setAvailableLanguages([]);
    }
  }, [languagesList]);

  // Dialog / Modal state
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editTargetCourseId, setEditTargetCourseId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal Form States
  const [modalSelectedCourseIds, setModalSelectedCourseIds] = useState([]);
  const [modalLanguages, setModalLanguages] = useState([]);

  // Handle open modal for generating new subtitles
  const handleOpenCreateModal = () => {
    setModalSelectedCourseIds([]);
    setModalLanguages(availableLanguages.map(l => {
      const isEnglish = l.name?.toLowerCase() === "english";
      return {
        ...l,
        selected: isEnglish || l.id === "spanish" || l.id === "hindi",
        disabled: isEnglish
      };
    }));
    setIsEditMode(false);
    setEditTargetCourseId(null);
    setOpenModal(true);
  };

  // Handle open modal for editing subtitle configuration of a specific course
  const handleOpenEditModal = (course) => {
    setModalSelectedCourseIds([course.id]);
    const chosen = Array.isArray(course.chosenLanguages) ? course.chosenLanguages : [];
    setModalLanguages(
      availableLanguages.map((lang) => {
        const isEnglish = lang.name?.toLowerCase() === "english";
        return {
          ...lang,
          selected: isEnglish || (lang.name && chosen.some(chosenName => chosenName.trim().toLowerCase() === lang.name.trim().toLowerCase())),
          disabled: isEnglish
        };
      })
    );
    setIsEditMode(true);
    setEditTargetCourseId(course.id);
    setOpenModal(true);
  };

  // Toggle languages in the modal dialog grid
  const handleToggleModalLanguage = (id) => {
    setModalLanguages((prev) =>
      prev.map((lang) => {
        if (lang.disabled || lang.name?.toLowerCase() === "english") {
          return lang;
        }
        return lang.id === id ? { ...lang, selected: !lang.selected } : lang;
      })
    );
  };

  // Multi-select change handler for selected courses in the modal
  const handleModalCourseChange = (event) => {
    const {
      target: { value },
    } = event;
    setModalSelectedCourseIds(typeof value === "string" ? value.split(",") : value);
  };

  // Save changes from the modal (Generate or Update)
  const handleSaveSubtitles = async () => {
    const selectedLangNames = modalLanguages
      .filter((l) => l.selected)
      .map((l) => l.name);

    const selectedLangCodes = modalLanguages
      .filter((l) => l.selected)
      .map((l) => l.code || l.name?.toLowerCase());

    if (modalSelectedCourseIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Courses Selected",
        text: "Please select at least one course to continue.",
        confirmButtonColor: "#831DDC",
      });
      return;
    }

    if (selectedLangCodes.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Languages Selected",
        text: "Please select at least one language.",
        confirmButtonColor: "#831DDC",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        course_ids: modalSelectedCourseIds,
        language_codes: selectedLangCodes,
      };

      setStopPolling(false);
      setErrorOpen(false);
      setSuccessOpen(false);

      const response = await generateSubtitles(payload);
      if (response && !response.error) {
        fetchSubtitles(currentPage, rowsPerPage, searchQuery);
      } else {
        // Fallback local update
        setCourses((prevCourses) =>
          prevCourses.map((course) => {
            if (modalSelectedCourseIds.includes(course.id)) {
              const total = selectedLangNames.length;
              let current = 0;
              let status = "Pending";

              if (isEditMode && course.id === editTargetCourseId) {
                current = Math.min(course.progressCurrent, total);
                status = current === total ? "Completed" : current === 0 ? "Pending" : "In Progress";
              } else {
                current = Math.floor(Math.random() * (total + 1));
                status = current === total ? "Completed" : current === 0 ? "Pending" : "In Progress";
              }

              return {
                ...course,
                chosenLanguages: selectedLangNames,
                progressCurrent: current,
                progressTotal: total,
                status: status,
              };
            }
            return course;
          })
        );
      }

      setOpenModal(false);

      Swal.fire({
        icon: "success",
        title: isEditMode ? "Subtitles Updated" : "Generating Subtitles",
        html: `Successfully configured subtitles for <b>${modalSelectedCourseIds.length} course(s)</b>.`,
        confirmButtonColor: "#831DDC",
      });
    } catch (err) {
      console.error("Error generating subtitles:", err);
      // Fallback local update
      setCourses((prevCourses) =>
        prevCourses.map((course) => {
          if (modalSelectedCourseIds.includes(course.id)) {
            const total = selectedLangNames.length;
            let current = 0;
            let status = "Pending";

            if (isEditMode && course.id === editTargetCourseId) {
              current = Math.min(course.progressCurrent, total);
              status = current === total ? "Completed" : current === 0 ? "Pending" : "In Progress";
            } else {
              current = Math.floor(Math.random() * (total + 1));
              status = current === total ? "Completed" : current === 0 ? "Pending" : "In Progress";
            }

            return {
              ...course,
              chosenLanguages: selectedLangNames,
              progressCurrent: current,
              progressTotal: total,
              status: status,
            };
          }
          return course;
        })
      );

      setOpenModal(false);

      Swal.fire({
        icon: "success",
        title: isEditMode ? "Subtitles Updated" : "Generating Subtitles",
        html: `Successfully configured subtitles for <b>${modalSelectedCourseIds.length} course(s)</b>.`,
        confirmButtonColor: "#831DDC",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableColumns = [
    {
      name: "course_id",
      label: "ID",
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "name",
      label: "Course Name",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const course = courses[tableMeta.rowIndex];
          if (!course) return value;
          return <Tooltip title={course.name || ""} arrow>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "10px",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {course.name?.length > 50 ? `${course.name.substring(0, 50)}...` : course.name}
            </Typography>
          </Tooltip>

        }
      }
    },
    {
      name: "chosenLanguages",
      label: "Chosen Languages",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          if (!value || value.length === 0) {
            return (
              <Typography sx={{ color: "#94A3B8", fontStyle: "italic" }}>
                None selected
              </Typography>
            );
          }
          return (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {value.map((lang, index) => (
                <Chip
                  key={index}
                  label={lang}
                  size="small"
                  sx={{

                    fontWeight: 600,

                    bgcolor: "rgba(131, 29, 220, 0.08)",
                    color: "#831DDC",
                    borderRadius: "6px",
                    px: 0.5,
                    border: "1px solid rgba(131, 29, 220, 0.05)",
                  }}
                />
              ))}
            </Box>
          );
        }
      }
    },
    {
      name: "progressTotal",
      label: "Subtitles Progress",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const course = courses[tableMeta.rowIndex];
          if (!course) return null;
          const completed = course.completed_videos !== undefined ? course.completed_videos : (course.progressCurrent || 0);
          const total = course.total_videos !== undefined ? course.total_videos : (course.progressTotal || 0);
          const progressPercent = total > 0 ? (completed / total) * 100 : 0;
          const showBuffer = course.status?.toLowerCase() === "processing" ||
            course.status?.toLowerCase() === "in progress";

          return (
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ width: 140 }}>
                <LinearProgress
                  variant={showBuffer ? "buffer" : "determinate"}
                  value={progressPercent}
                  valueBuffer={
                    showBuffer
                      ? Math.min(100, progressPercent + 15)
                      : progressPercent
                  }
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#E2E8F0",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor: "#831DDC",
                    },
                    "& .MuiLinearProgress-bar1Buffer": {
                      borderRadius: 3,
                      backgroundColor: "#831DDC",
                    },
                    "& .MuiLinearProgress-bar2Buffer": {
                      borderRadius: 3,
                      backgroundColor: "rgba(131, 29, 220, 0.3)",
                    },
                    "& .MuiLinearProgress-dashed": {
                      display: showBuffer ? "block" : "none",
                      backgroundImage:
                        "radial-gradient(rgba(131, 29, 220, 0.4) 0%, rgba(131, 29, 220, 0.4) 16%, transparent 42%)",
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: "#1E293B", lineHeight: 1.1 }}>
                  {total > 0 ? `${completed} / ${total}` : "0 / 0"}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color:
                      course.status === "Completed"
                        ? "#16A34A"
                        : course.status === "Processing"
                          ? "#D97706"
                          : "#64748B",
                    mt: 0.3,
                  }}
                >
                  {course.status}
                </Typography>
              </Box>
            </Box>
          );
        }
      }
    },
    {
      name: "id",
      label: "Edit",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          const course = courses.find((c) => c.id === value);
          if (!course) return null;
          return (
            <IconButtonComponent
              btnType={"editbtn"}
              onclick={() => handleOpenEditModal(course)}
              size="small"
            >
            </IconButtonComponent>
          );
        }
      }
    }
  ];

  return (
    <>
      <Box className="main-screen-container">
        {/* Title & Top Action Bar */}
        <Grid container direction="row" justifyContent="space-between" alignItems={'center'} mb={4}>
          <Grid item>
            <HeadingComponent
              text={"Subtitle"}
              fontweigth={600}
              size={40}
              fontfamily={"Montserrat"}
            />
          </Grid>
          {/* <Grid item>
            <TextIconButtonComponent
              btnText={"Generate Subtitles"}
              icon={faClosedCaptioning}
              onclick={handleOpenCreateModal}
            />
          </Grid> */}


        </Grid>

        {/* Main Table Content */}
        <Box className="common-admin-content-wrap">
          <PaginationTable filter={false}
            columns={tableColumns}
            data={courses}
            totalItems={totalItems}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSearchChange={handleSearchChange}
            name={"subtitles"}
          />
        </Box>
      </Box>
      {/* Modal Dialog: Generate Subtitles */}
      <DialogComponent
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={isEditMode ? "Update Subtitles" : "Generate Subtitles"}
        maxwidth="md"
        backgroundColor="#831DDC"
        btntextone={isEditMode ? "Update" : "Generate Subtitles"}
        onclickone={handleSaveSubtitles}
        btntexttwo="Cancel"
        isBtnOneLoading={isSubmitting}
        isBtnOneDisabled={isSubmitting}
      >
        <DialogContent sx={{ p: 4 }}>
          {/* Select Courses Area */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 700, color: "#1E293B", mb: 1 }}>
              Course Name
            </Typography>
            {isEditMode ? (
              <Typography>
                {courses.find((c) => c.id === modalSelectedCourseIds[0])?.name || ""}
              </Typography>
            ) : (
              <Select
                multiple
                displayEmpty
                value={modalSelectedCourseIds}
                onChange={handleModalCourseChange}
                disabled={isEditMode}
                input={<OutlinedInput size="small" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return (
                      <span style={{ color: "#94a3b8", }}>
                        Select one or more courses
                      </span>
                    );
                  }
                  return (
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((id) => {
                        const course = courses.find((c) => c.id === id);
                        return course ? (
                          <Chip
                            key={id}
                            label={course.name}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              bgcolor: "#F1F5F9",
                            }}
                          />
                        ) : null;
                      })}
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 250,
                      borderRadius: "8px",
                    },
                  },
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: "#ffffff",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#831DDC",
                  },
                }}
                fullWidth
              >
                {courses.map((course) => (
                  <MenuItem
                    key={course.id}
                    value={course.id}
                  >
                    <Checkbox
                      checked={modalSelectedCourseIds.indexOf(course.id) > -1}
                      sx={{
                        color: "#cbd5e1",
                        "&.Mui-checked": {
                          color: "#831DDC",
                        },
                      }}
                    />
                    <ListItemText
                      primary={course.name}
                    />
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>

          {/* Select Languages Area */}
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#1E293B", mb: 1.5 }}>
              Select Languages
            </Typography>

            <Grid container spacing={1.5}>
              {modalLanguages.map((lang) => (
                <Grid item xs={12} sm={6} md={3} key={lang.id}>
                  <Box
                    onClick={() => {
                      const isDisabled = lang.disabled || lang.name?.toLowerCase() === "english";
                      if (!isDisabled) {
                        handleToggleModalLanguage(lang.id);
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: lang.selected ? "1.5px solid #831DDC" : "1px solid #E2E8F0",
                      borderRadius: "8px",
                      px: 1.8,
                      py: 1,
                      cursor: (lang.disabled || lang.name?.toLowerCase() === "english") ? "not-allowed" : "pointer",
                      bgcolor: lang.selected ? "rgba(131, 29, 220, 0.02)" : "#ffffff",
                      opacity: (lang.disabled || lang.name?.toLowerCase() === "english") ? 0.7 : 1,
                      transition: "all 0.15s ease",
                      ...(!(lang.disabled || lang.name?.toLowerCase() === "english") && {
                        "&:hover": {
                          borderColor: "#831DDC",
                          bgcolor: "rgba(131, 29, 220, 0.01)",
                        }
                      })
                    }}
                  >
                    <Checkbox
                      checked={lang.selected}
                      disabled={lang.disabled || lang.name?.toLowerCase() === "english"}
                      onChange={() => {
                        const isDisabled = lang.disabled || lang.name?.toLowerCase() === "english";
                        if (!isDisabled) {
                          handleToggleModalLanguage(lang.id);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: "#CBD5E1",
                        p: 0.5,
                        mr: 1,
                        "&.Mui-checked": {
                          color: "#831DDC",
                        },
                      }}
                    />
                    <Typography sx={{ fontWeight: 500, color: "#334155" }}>
                      {lang.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Information / Description Footer Note Box */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "rgba(131, 29, 220, 0.04)",
              border: "1px solid rgba(131, 29, 220, 0.1)",
              borderRadius: "8px",
              p: 2,
              mt: 4,
            }}
          >
            <InfoIcon sx={{ color: "#831DDC", }} />
            <Typography sx={{ color: "#5B21B6", fontWeight: 500 }}>
              Subtitles will be generated for all videos in the selected courses in the chosen languages.
            </Typography>
          </Box>
        </DialogContent>
      </DialogComponent>

      <SubtitleProgressBackdrop
        open={isPollingActive}
        completedItems={processingInfo.completed}
        totalItems={processingInfo.total}
        progressPercent={processingInfo.percent}
      />

      <PopUpMessageComponent
        open={errorOpen}
        type="error"
        message="An error occurred while generating subtitles. Please try again."
        btntext="Close"
        onclick={() => setErrorOpen(false)}
        onclose={() => setErrorOpen(false)}
      />

      <PopUpMessageComponent
        open={successOpen}
        type="success"
        message="Subtitles have been successfully generated."
        btntext="OK"
        onclick={() => setSuccessOpen(false)}
        onclose={() => setSuccessOpen(false)}
      />
    </>
  );
};

export default connect(
  (state) => ({
    courseSubtitleList: state.video.get("courseSubtitleList"),
    languagesList: state.video.get("languagesList"),
  }),
  {
    fetchCourseSubtitleList: Actions.video.fetchCourseSubtitleList,
    generateSubtitles: Actions.video.generateSubtitles,
    fetchLanguages: Actions.video.fetchLanguages,
  }
)(VideoSubtitleScreen);
